import { StandardModal } from "@classdojo/web";
import { TextArea, TextField } from "@classdojo/web/nessie";
import { TEXT_FIELD_HEIGHT } from "@classdojo/web/nessie/components/TextField";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { DDSButton } from "@web-monorepo/dds";
import { AT, autoTranslate } from "@web-monorepo/vite-auto-translate-plugin/runtime";
import { IdeasCloseIcon } from "components/IdeasIcons";
import React from "react";
import { ToolListItem, ToolPayload } from "src/pods/toolkitData/components/EditableSetup";

type DirectionsEditorProps = {
  onEdit: (updatedPayload: ToolPayload) => void;
  payload: ToolPayload;
  setFocusOnParent?: (focus: boolean) => void;
};

type DirectionsEditorState = {
  askForNewItem: boolean;
  newItemText: string | null;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class DirectionsEditor extends React.Component<DirectionsEditorProps, DirectionsEditorState> {
  constructor(props: DirectionsEditorProps) {
    super(props);
    this.state = {
      askForNewItem: false,
      newItemText: null,
    };
  }

  render() {
    const { askForNewItem, newItemText } = this.state;
    let { list, title } = this.props.payload || {};
    list = list || [];
    title = title || "";

    return (
      <article>
        <section>
          <TextField label={autoTranslate("Activity name (optional)")} value={title} onChange={this.onTitleChange} />
          <ol sx={listStyle}>
            {list.map((item, idx) => (
              <li key={idx} sx={itemStyle}>
                <span sx={itemCountStyle}>
                  <AT>{idx + 1}.</AT>
                </span>
                <p sx={itemTextStyle}>{item.text}</p>
                <button
                  data-name="directions_editor:list:ideas_close_icon"
                  sx={deleteItemButton}
                  aria-label={autoTranslate("Delete item")}
                  onClick={() => this.deleteItem(item)}
                >
                  <IdeasCloseIcon />
                </button>
              </li>
            ))}
            <li sx={itemStyle}>
              <span sx={addItemCountStyle}>
                <AT>{list.length + 1}.</AT>
              </span>
              <DDSButton
                data-name="directions_editor:render:directions_add_item"
                sx={addItemTextStyle}
                onClick={this.openNewItemInput}
                variant="ghost"
              >
                Add item
              </DDSButton>
            </li>
          </ol>
        </section>

        {askForNewItem && (
          <StandardModal
            data-name="directions_editor:render:standard_modal"
            headerStyle={{
              display: "none",
            }}
            primaryButton={{
              text: autoTranslate("Add"),
              props: {
                "data-name": "directions_editor:ask_for_new_item:add",
                disabled: !newItemText,
                onClick: () => {
                  this.props.setFocusOnParent?.(true);
                  this.addNewItem(this.state.newItemText!);
                  this.closeNewItemInput();
                },
              },
            }}
            secondaryButton={{
              text: autoTranslate("Cancel"),
              props: {
                onClick: () => {
                  this.props.setFocusOnParent?.(true);
                  this.closeNewItemInput();
                },
                "data-name": "directions_editor:ask_for_new_item:cancel",
              },
            }}
            useDDSButtonForSecondary
            useDDSButtonForPrimary
          >
            <TextArea
              label={autoTranslate("Enter new item's name or description")}
              sx={noteAreaStyle}
              onChange={(value) => this.setState({ newItemText: value })}
              fixedHeight={TEXT_FIELD_HEIGHT * 2}
              autoFocus
            />
          </StandardModal>
        )}
      </article>
    );
  }

  onChange = (update: ToolPayload) => {
    const updatedPayload = Object.assign({ list: [], title: "" }, this.props.payload, update);
    this.props.onEdit(updatedPayload);
  };

  openNewItemInput = () => {
    this.props.setFocusOnParent?.(false);
    this.setState({ askForNewItem: true, newItemText: null });
  };

  closeNewItemInput = () => {
    this.setState({ askForNewItem: false, newItemText: null });
  };

  onTitleChange = (event: string) => {
    this.onChange({ title: String(event) });
  };

  addNewItem = (text: string) => {
    const list = this.props?.payload?.list || [];
    this.onChange({ list: [...list, { text }] });
  };

  deleteItem = (item: ToolListItem) => {
    this.onChange({ list: this.props.payload.list?.filter((i) => i !== item) });
  };
}

const listStyle: ThemeUIStyleObject = {
  padding: RAW_cssValue("0"),
  margin: RAW_cssValue(0),
  listStyle: "none",
  textAlign: "left",
};

const itemStyle: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  minHeight: "64px",
  margin: RAW_cssValue("16px 0"),
  border: "dt_card",
  borderRadius: "dt_radius_s",
};

const itemCountStyle: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "42px",
  height: "64px",
  padding: RAW_cssValue("20px 10px"),
  textAlign: "right",
  verticalAlign: "top",
  fontSize: "18px",
  fontWeight: "600",
};

const itemTextStyle: ThemeUIStyleObject = {
  width: "100%",
  minHeight: "64px",
  margin: RAW_cssValue("0"),
  padding: RAW_cssValue("20px"),
  border: "none",
  borderLeft: RAW_cssValue("1px solid rgba(0,0,0,0.12)"),
  textAlign: "left",
  fontSize: "17px",
  lineHeight: "24px",
  backgroundColor: "transparent",
};

const addItemCountStyle: ThemeUIStyleObject = {
  ...itemCountStyle,
  color: "dt_content_primary",
};

const addItemTextStyle: ThemeUIStyleObject = {
  ...itemTextStyle,
  textAlign: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "dt_content_primary",
  borderRadius: "0",
};

const noteAreaStyle: ThemeUIStyleObject = {
  minHeight: "104px",
  fontSize: "17px",
  marginTop: RAW_cssValue("5px"),
};

const deleteItemButton: ThemeUIStyleObject = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "36px",
  height: "52px",
  padding: RAW_cssValue("12px 20px"),
  backgroundColor: "transparent",
  border: "none",
};
